/* Read authoritative campaign pricing independently of the app widget's
 * asynchronous initialization and modal button state. */
if (!customElements.get('industrial-group-price')) {
  // Collection pages can contain hundreds of cards. Limit simultaneous app
  // requests; off-screen cards do not request prices until they enter view.
  let activeRequests = 0;
  const requestQueue = [];
  const acquireRequest = () => new Promise(resolve => {
    const start = () => { activeRequests++; resolve(() => {
      activeRequests--;
      requestQueue.shift()?.();
    }); };
    if (activeRequests < 4) start(); else requestQueue.push(start);
  });
  customElements.define('industrial-group-price', class extends HTMLElement {
    connectedCallback() {
      this.cardMode = this.hasAttribute('data-card-price');
      this.form = this.closest('.shopify-section')?.querySelector('[data-product-form]');
      this.regular = this.querySelector('[data-product-price]');
      this.panel = this.querySelector('[data-group-price-panel]');
      this.onChange = () => this.sync();
      this.form?.addEventListener('change', this.onChange);
      if (this.cardMode && typeof IntersectionObserver !== 'undefined') {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries[0].isIntersecting;
          if (this.inView) this.sync();
        }, {rootMargin: '150px'});
        // Observe the card, which has a stable box even while its price
        // children are still being parsed or inserted by a section render.
        this.observer.observe(this.closest('[data-wf-product-card]') || this);
      } else {
        this.inView = true;
        this.sync();
      }
      this.timer = setInterval(() => { if (!document.hidden && this.inView) this.sync(); }, 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.observer?.disconnect();
      this.controller?.abort();
      this.form?.removeEventListener('change', this.onChange);
    }

    showRegular() {
      if (this.panel) this.panel.hidden = true;
      if (this.regular) this.regular.hidden = false;
    }

    setText(selector, value) {
      const node = this.querySelector(selector);
      if (node && node.textContent !== value) node.textContent = value;
    }

    sync() {
      // Custom elements can connect before the parser has inserted their
      // children (cached scripts / section rendering). Reacquire the price
      // nodes instead of permanently retaining null from connectedCallback.
      this.regular = this.querySelector('[data-product-price]');
      this.panel = this.querySelector('[data-group-price-panel]');
      if ((!this.form && !this.cardMode) || !this.regular || !this.panel) return;
      const block = document.querySelector('.group-buying-block');
      const config = this.cardMode ? {
        apiEndpoint: '/apps/groupbuy', productId: this.dataset.productId,
        marketId: this.dataset.marketId, countryCode: this.dataset.country,
        currency: this.dataset.currency, locale: document.documentElement.lang
      } : window.groupBuyingConfig?.[block?.dataset.sectionId];
      const variantId = String(this.form?.querySelector('[name="id"]')?.value || this.dataset.initialVariant);
      const option = this.form?.querySelector('[data-product-variant-select]')?.selectedOptions[0];
      const cents = Number(option ? option.dataset.priceRaw : this.dataset.initialPrice);
      const currency = window.Shopify?.currency?.active || config?.currency;
      const key = [variantId, cents, currency, config?.marketId, config?.countryCode].join(':');
      const changed = key !== this.key;
      if (changed) {
        this.key = key;
        this.controller?.abort();
        this.pending = false;
        this.nextRefresh = 0;
        this.expiresAt = 0;
        this.showRegular();
      }
      if (this.expiresAt && Date.now() >= this.expiresAt) this.showRegular();
      if (!config?.apiEndpoint || !config.productId || !currency ||
          !Number.isFinite(cents) || cents <= 0 || this.form?.classList.contains('is-quote-only')) {
        this.showRegular();
        return;
      }
      if (this.pending || Date.now() < (this.nextRefresh || 0)) return;
      this.loadPrice(config, variantId, cents, currency, key);
    }

    async loadPrice(config, variantId, cents, currency, key) {
      const controller = new AbortController();
      this.controller = controller;
      this.pending = true;
      const params = new URLSearchParams({product_id: config.productId, variant_id: variantId, currency_code: currency});
      if (config.marketId) params.set('market_id', String(config.marketId).split('/').pop());
      if (config.countryCode) params.set('country', config.countryCode);
      if (config.customerEmail) params.set('customer_email', config.customerEmail);
      const release = await acquireRequest();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        if (controller.signal.aborted || key !== this.key) return;
        const response = await fetch(`${config.apiEndpoint}/campaign-status?${params}`, {signal: controller.signal});
        if (!response.ok) throw new Error('Campaign price request failed');
        const data = await response.json();
        if (key !== this.key || controller.signal.aborted) return;
        const campaign = data.campaign;
        const pricing = (campaign?.variantPricing || data.variantPricing)?.[variantId];
        if (!data.success || campaign?.status !== 'active' || campaign.isActive === false ||
            !pricing || !Number.isFinite(pricing.original) || pricing.original <= 0 ||
            !Number.isFinite(pricing.group) || pricing.group <= 0 || pricing.group >= pricing.original) {
          this.showRegular();
          return;
        }
        // Match the installed widget: apply its rounded discount percentage
        // to Liquid's market-converted variant price, never base-currency money.
        const discount = Math.round((pricing.original - pricing.group) / pricing.original * 100);
        const groupCents = Math.round(cents * (100 - discount) / 100);
        if (groupCents <= 0 || groupCents >= cents) {
          this.showRegular();
          return;
        }
        // timeRemaining is server-relative, so an incorrect device clock does
        // not immediately erase an otherwise active offer.
        const remaining = campaign.timeRemaining;
        this.expiresAt = Number.isFinite(remaining)
          ? Date.now() + Math.max(0, remaining) * 1000 : Date.parse(campaign.endDate);
        if (Number.isFinite(this.expiresAt) && this.expiresAt <= Date.now()) {
          this.showRegular();
          return;
        }
        const format = value => new Intl.NumberFormat(config.locale || document.documentElement.lang || 'en', {
          style: 'currency', currency, currencyDisplay: 'code'
        }).format(value / 100);
        this.setText('[data-group-price-amount]', format(groupCents));
        this.setText('[data-group-price-original]', this.cardMode ? this.dataset.originalPrice : optionPrice(this.form) || this.regular.textContent.trim());
        this.setText('[data-group-price-saving]', this.dataset.savingLabel.replace('[amount]', format(cents - groupCents)));
        const count = Number(campaign.minimumParticipants);
        this.setText('[data-group-price-participants]', Number.isInteger(count) && count > 1
          ? this.dataset.participantsLabel.replace('[count]', String(count)) : '');
        this.regular.hidden = true;
        this.panel.hidden = false;
      } catch (error) {
        if (key === this.key) this.showRegular();
      } finally {
        clearTimeout(timeout);
        release();
        if (this.controller === controller) {
          this.pending = false;
          this.nextRefresh = Date.now() + 60000;
        }
      }
    }
  });
}

function optionPrice(form) {
  return form.querySelector('[data-product-variant-select]')?.selectedOptions[0]?.dataset.price;
}
